"use client";

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react";
import { AdSlots, type AdType } from "./adConfig";

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

const PUBLISHER_ID = "ca-pub-9905237477443613";
const AD_LOAD_TIMEOUT = 3000;

export type GoogleAdHandle = {
  showAds: (slotKey: AdType) => Promise<boolean>;
};

const GoogleAdModal = forwardRef<GoogleAdHandle>((_, ref) => {
  const [visible, setVisible] = useState(false);
  const [activeSlot, setActiveSlot] = useState<AdType | null>(null);
  const [adDisplayed, setAdDisplayed] = useState(false);

  const insRef = useRef<HTMLModElement | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);
  const rafRef = useRef<number | null>(null);
  const autoCloseRef = useRef<NodeJS.Timeout | null>(null);
  const adCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const cleanup = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    
    if (autoCloseRef.current) {
      clearTimeout(autoCloseRef.current);
      autoCloseRef.current = null;
    }

    if (adCheckIntervalRef.current) {
      clearInterval(adCheckIntervalRef.current);
      adCheckIntervalRef.current = null;
    }
    
    setVisible(false);
    setActiveSlot(null);
    document.body.style.overflow = "";
  }, []);

  const closeAndResolve = useCallback((adWasDisplayed: boolean) => {
    cleanup();
    resolverRef.current?.(adWasDisplayed);
    resolverRef.current = null;
  }, [cleanup]);

  const checkAdDisplayed = useCallback(() => {
    const ins = insRef.current;
    if (!ins) return false;

    const status = ins.getAttribute("data-adsbygoogle-status");
    const hasAdContent = ins.children.length > 0;
    
    if (status === "done" && hasAdContent) {
      if (adCheckIntervalRef.current) {
        clearInterval(adCheckIntervalRef.current);
        adCheckIntervalRef.current = null;
      }
      return true;
    }
    
    return false;
  }, []);

  const pushAd = useCallback(() => {
    const ins = insRef.current;
    if (!ins || !activeSlot) return;

    if (ins.offsetWidth === 0) {
      rafRef.current = requestAnimationFrame(pushAd);
      return;
    }

    const status = ins.getAttribute("data-adsbygoogle-status");
    if (status === "done") return;

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      
      adCheckIntervalRef.current = setInterval(() => {
        if (checkAdDisplayed()) {
          setAdDisplayed(true);
          if (adCheckIntervalRef.current) {
            clearInterval(adCheckIntervalRef.current);
            adCheckIntervalRef.current = null;
          }
        }
      }, 100);
      
    } catch (e) {
      closeAndResolve(false);
    }
  }, [activeSlot, checkAdDisplayed, closeAndResolve]);

  useImperativeHandle(ref, () => ({
    showAds: async (slotKey: AdType) => {
      const config = AdSlots[slotKey];
      if (!config || config.kind !== "modal") {
        return false;
      }
      
      return new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setActiveSlot(slotKey);
        setVisible(true);
        setAdDisplayed(false);
      });
    }
  }), []);

  useEffect(() => {
    if (visible) {
      rafRef.current = requestAnimationFrame(pushAd);
      
      autoCloseRef.current = setTimeout(() => {
        const displayed = checkAdDisplayed();
        closeAndResolve(displayed);
      }, AD_LOAD_TIMEOUT);
      
      return () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        if (autoCloseRef.current) {
          clearTimeout(autoCloseRef.current);
          autoCloseRef.current = null;
        }
        if (adCheckIntervalRef.current) {
          clearInterval(adCheckIntervalRef.current);
          adCheckIntervalRef.current = null;
        }
      };
    }
  }, [visible, pushAd, closeAndResolve, checkAdDisplayed]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && visible) {
        closeAndResolve(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [visible, closeAndResolve]);

  if (!visible || !activeSlot) return null;

  const config = AdSlots[activeSlot];

  return (
    <div 
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={() => closeAndResolve(false)}
    >
      <ins
        key={activeSlot}
        ref={insRef}
        className="adsbygoogle"
        style={{ 
          display: "block", 
          width: "300px", 
          height: "250px", 
          ...config.style 
        }}
        data-ad-client={PUBLISHER_ID}
        data-ad-slot={config.slot}
        data-ad-format={config.format || "auto"}
        data-full-width-responsive="true"
      />
    </div>
  );
});

GoogleAdModal.displayName = "GoogleAdModal";
export default GoogleAdModal;
